// ---------------------------------------------------------------------------
// Legal threats — where the opponent can strike from this round, and how hard
// ---------------------------------------------------------------------------
//
// legalThreats(board) → per corridor, the most strength the side owning
// `board` (its start-of-round board) can attack with this round:
//
//   0       the corridor can't attack at all: nothing started the round there
//           (no veteran, no mature brood), or an airdrop landed there (a
//           corridor holding a fresh drone can't attack, and an airdropped
//           drone can't be moved out of it)
//   else    its veterans and mature broods (calculateCombatStrength), plus one
//           for each neighbour holding a veteran that could move in, as far
//           as the corridor has room for them
//
// It is an upper bound: a neighbour can send its one mover to one side only,
// and players often don't move one in. { movers: false } gives what is
// already there — veterans and mature broods alone.
//
// stakeInReach(hf, board, col, margin, lost) → what a crater won in `col` with
// `margin` could reach of ours, scored for the deploys' priorities: every
// standing structure with a live cell in reach (more at match point, where
// losing one loses the war, and more for one a single cell from dead), a
// brood under construction (one cell kills it) and, less, a mature one.
// A crater lands inside its corridor but spills into the neighbours
// (craterFootprint): one subplot column deep at margin 1, two at 2-3, the
// whole corridor at 4 or more — never two corridors away.

import { rules } from '../config.js'
import {
    calculateCombatStrength, basesOf, moveUnit, deployArmy, canAttack, canPlaceDropZone, requiredDropZones,
} from '../gameLogic.js'
import { plotSubIndices, plotOf, isStructureDestroyed } from '../subplotGrid.js'
import { validateOrders } from '../validateOrders.js'
import { craterFootprint } from '../subplotDamage.js'
import { baseFootprint, isBroodDead } from '../subplotBases.js'

const corridorCells = (col) => [0, 1, 2, 3, 4].flatMap(row => plotSubIndices(col, row))
const room = (bf) => bf.maxArmyQty - ((bf.armyQty || 0) + (bf.newArmies || 0) + (bf.movedInArmies || 0))

export function canStrikeFrom(bf) {
    if ((bf.newArmies || 0) > 0) return false
    return (bf.armyQty || 0) > 0 || (bf.fortCount || 0) > 0
}

export function legalThreats(board, { movers = true } = {}) {
    return board.map((bf, c) => {
        if (!canStrikeFrom(bf)) return 0
        if (!movers) return calculateCombatStrength(bf)
        const n = [c - 1, c + 1].filter(k => (board[k]?.armyQty || 0) > 0).length
        return calculateCombatStrength(bf) + Math.min(n, Math.max(0, room(bf)))
    })
}

// Every cell a crater won in `col` can dig at `margin`, cached.
const REACH = new Map()
export function reachOf(col, margin) {
    const m = Math.max(1, Math.min(5, margin))
    const key = `${col}:${m}`
    if (!REACH.has(key)) REACH.set(key, new Set(corridorCells(col).flatMap(s => craterFootprint(s, m))))
    return REACH.get(key)
}

export function stakeInReach(hf, board, col, margin, lost = 0) {
    if (margin <= 0) return 0
    const reach = reachOf(col, margin)
    const matchPoint = lost >= rules().structuresToWin - 1
    let v = 0
    for (const [id, st] of Object.entries(hf?.structures ?? {})) {
        if (isStructureDestroyed(hf, id)) continue
        const live = st.footprint.filter(s => !hf.subplots[s].destroyed)
        if (!live.some(s => reach.has(s))) continue
        v += (matchPoint ? 3 : 1) * (live.length === 1 ? 1.5 : 1)
    }
    for (const c of [col - 1, col, col + 1]) {
        if (!board[c]) continue
        for (const b of basesOf(board[c])) {
            if (!(baseFootprint(b.centerS) ?? []).some(s => reach.has(s))) continue
            v += b.mature ? 0.5 : 1.5
        }
    }
    return v
}

// ---------------------------------------------------------------------------
// pressureOrders — the opponent's worst case, for the search's futures
// ---------------------------------------------------------------------------
//
// pressureOrders(them, ours, rank) → orders for the opponent `them` ({ board,
// homefront }, a sampled Grid) that strike as hard as they legally can from
// the corridor ranked `rank` (0 = the most dangerous) by what a crater won
// there could reach of ours (stakeInReach against what we show there now):
// every neighbouring veteran moved in, the fresh drones deployed elsewhere,
// the shot aimed at the most of ours it can cover. `ours` is { board,
// homefront, structuresLost } — our own side, which the search knows whole:
// this is the worst case, not a guess at their aim. null when no corridor
// can strike, or the plan isn't legal. `ours.board` may be a plan's board:
// then the danger is ranked against that plan's deploys, moves and the broods
// it lays (the search's per-plan worst case, `planWorstCase`).
//
// The player model calls some strikes rare (an attack from a brood corridor,
// say), and the search's few futures can miss them entirely; a couple of
// these futures make sure the threats that can happen are priced.

const clone = (v) => JSON.parse(JSON.stringify(v))
const shown = (bf) => calculateCombatStrength(bf) + (bf.newArmies || 0) + (bf.movedInArmies || 0)

export function dangerRanking(theirBoard, ours) {
    const T = legalThreats(theirBoard)
    return T.map((t, c) => ({ c, t, v: t > 0 ? stakeInReach(ours.homefront, ours.board, c, Math.max(1, t - shown(ours.board[c])), ours.structuresLost ?? 0) : -1 }))
        .filter(x => x.t > 0)
        .sort((a, b) => b.v - a.v || b.t - a.t)
}

export function pressureOrders(them, ours, rank = 0) {
    const pickC = dangerRanking(them.board, ours)[rank]
    if (!pickC) return null
    const t = pickC.c
    let board = clone(them.board)
    for (const m of [t - 1, t + 1]) {
        if (!board[m] || !(board[m].armyQty > 0)) continue
        const r = moveUnit(board, board[m].id, m < t ? 'right' : 'left')
        if (r.success) board = r.boardArr
    }
    if (!canAttack(board[t]).valid) return null
    let owed = rules().armiesPerRound
    const others = board.map((_, c) => c).filter(c => c !== t).sort((a, b) => shown(ours.board[b]) - shown(ours.board[a]))
    for (const c of others) {
        let r
        while (owed > 0 && (r = deployArmy(board, board[c].id, owed)).success) { board = r.boardArr; owed = r.armiesToDeploy }
    }
    board[t] = { ...board[t], isAttacking: true }
    // The aim: the cell whose crater covers the most of ours.
    const hf = ours.homefront
    const margin = Math.max(1, Math.min(5, calculateCombatStrength(board[t]) + (board[t].movedInArmies || 0) - shown(ours.board[t])))
    const worth = new Map()
    for (const [id, st] of Object.entries(hf?.structures ?? {})) {
        if (isStructureDestroyed(hf, id)) continue
        for (const s of st.footprint) if (!hf.subplots[s].destroyed) worth.set(s, 1)
    }
    for (const [id, b] of Object.entries(hf?.broods ?? {})) {
        if (isBroodDead(hf, id)) continue
        for (const s of b.footprint) if (!hf.subplots[s].destroyed) worth.set(s, b.underConstruction ? 2 : 0.5)
    }
    // Broods a plan lays aren't on the Grid until resolution: read them off
    // the board.
    for (const bf of ours.board) for (const b of basesOf(bf)) {
        if (!b.mature) for (const s of baseFootprint(b.centerS) ?? []) worth.set(s, 2)
    }
    let aim = corridorCells(t)[22], best = -1
    for (const s of corridorCells(t)) {
        const v = craterFootprint(s, margin).reduce((a, x) => a + (worth.get(x) ?? 0), 0)
        if (v > best) { best = v; aim = s }
    }
    const targetPlots = Array(board.length).fill(null)
    const targetSubplots = Array(board.length).fill(null)
    targetSubplots[t] = aim
    targetPlots[t] = plotOf(aim).row
    const dropZones = []
    const open = board.reduce((n, bf) => n + Math.max(0, bf.maxArmyQty - ((bf.armyQty || 0) + (bf.newArmies || 0) + (bf.movedInArmies || 0))), 0)
    const owedZones = Math.min(requiredDropZones(board), open)
    for (let c = 0; dropZones.length < owedZones && c < board.length; c++) {
        while (dropZones.length < owedZones && canPlaceDropZone(board, c, dropZones).valid) dropZones.push(c)
    }
    const orders = { board, targetPlots, targetSubplots, dropZones, rockets: [], drones: [] }
    return validateOrders({ ...them, yield: 0 }, orders).ok ? orders : null
}
